import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLayoutContext } from "../../layouts/general-layout";
import { RouterConstant } from "../../routers/index";
import { OTPInput, SlotProps } from 'input-otp';
import Button from '../../components/button';
import { LoginService } from "../../services/login/login.services";
import "./user-registration-otp.scss";
import LoginHeader from '../../components/login-header';
import { useUserStore } from "../../utils/userStore";
import { gamificationStore } from "../../utils/gamificationStore";
import { getRequiredRoute } from "../../utils/navigationUtils";

export default function OTPPage() {
  const { t } = useLayoutContext();
  const navigate = useNavigate();
  const location = useLocation();
  const navigation_source: 'register' | 'reset' = location.state?.navigation_source || 'register'; 
  const { ROUTERS_PATH } = RouterConstant;
  const loginService = new LoginService();
  const { userInfo: formData, setUserInfo, setAttributes, resetUser, setJustLoggedIn, setLoggedIn } = useUserStore();

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [countdown, setCountdown] = useState(300);
  const [hasReachedLimit, setHasReachedLimit] = useState(false);

  const errorCodeMessages: Record<number, string> = {

    1: t("codeNotFound"),
    2: t('codeAlreadyUsed'),
    3: t("codeExpired"),
    4: t("incorrectCode"),
    5: t("reachedMaxRequests"),
    6: t("emailInvalid"),
    99: t("unexpectedError")    
  };

  // On mount, send OTP and start countdown
  useEffect(() => {
    sendOtp();
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown === 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const formatTime = (seconds: number) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  const sendOtp = async () => {
    if (!formData.email) {
      setError(t("provideEmail"));
      return;
    }
  
    try {
      setError('');
      await loginService.requestOtp(formData.email, formData.first_name || '', navigation_source);
    } catch (err: any) {
      let message = t("errorSendingCode");
      if (err.response?.data?.detail?.error_code === 1) {
        message = t("emailInvalid");
      } else if (err.response?.data?.detail?.error_code === 2) {
        setHasReachedLimit(true)
        message = t("reachedMaxRequests");
      } else if (err.response?.data?.detail?.error_code === 3) {
        message = t("emailAlreadyExist");
      }

      setError(message);
    }
  };

  const handleResendOtp = () => {
    setCountdown(300);
    sendOtp();
    setVerifyError('')
    setError('')
  };

  const handleBackClick = () => {
    if (navigation_source == 'reset'){
      navigate(ROUTERS_PATH.LOGIN);
    } else {
      navigate(ROUTERS_PATH.USERREGISTRATIONSTEP1);
    }
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    setVerifyError("");
    try {
      let response;
      if (navigation_source === 'reset') {
        response = await loginService.verifyResetPasswordOtp(formData.email || '', otp);
      } else {
        response = await loginService.verifyOtp(
          formData.email || '',
          otp,
          formData.first_name || '',
          formData.last_name || '',
          formData.userType || 4,
          formData.password || ''
        );
      }
      const user = response?.user;
      if (user?.id) {
          const userTypeAttr = response.attributes?.find((attr: any) => attr.att_name === 'user_type')?.att_value;
          setUserInfo({
            userId: user?.id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            phone: user.phone,
            userType: userTypeAttr ?? formData.userType,
            is_access: user?.is_access,
            org_id: user?.org_id,
            org_type: user?.org_type,
          });
          setAttributes(user.attributes || []);
          if (navigation_source === 'reset') {
            navigate(ROUTERS_PATH.RESETPASSWORD, { state: { email: formData.email } });
          } else {
            setJustLoggedIn(true);
            setLoggedIn(true);
            
            if (response.gamification_state) {
              gamificationStore.getState().setGamificationState({
                totalPoints: response.gamification_state.total_points,
                levelKey: response.gamification_state.level_key,
                levelLabel: response.gamification_state.level_label,
                levelIcon: response.gamification_state.level_icon || '',
                classMedian: response.gamification_state.class_median || 0,
                currentStreak: response.gamification_state.current_streak,
                longestStreak: response.gamification_state.longest_streak,
                lastActivityDate: response.gamification_state.last_activity_date,
              }, true);
            }

            const nextRoute = getRequiredRoute(
              useUserStore.getState(), 
              { forceProfile: navigation_source === 'register' }
            );
            navigate(nextRoute);
          }
      }

    } catch (err: any) {
      setOtp("");
      const code = err.response?.data?.detail?.error_code ?? err.response?.data?.error_code ?? 99;
      const message = errorCodeMessages[code] || err.customMessage || t("unexpectedError");
      setVerifyError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value: string) => {
    if (verifyError) {
      setVerifyError("");
    }

    const numericValue = value.replace(/\D/g, '').slice(0, 5);
    setOtp(numericValue);
  };

  return (
    <div className="full-head-template login-page">
      <LoginHeader
        handleBackClick={handleBackClick}
        noImg
        title={t('WhatsSentCode')}
        subtitle={t('OTPSentToEmail')}
      />

      <div className='login-page-form'>
        <div>
          <div className='user-info-container'>
            <p className='user-email-container'>{formData.email}</p>
            <button
              onClick={handleBackClick}
              className='change-email-button'
            >
              {t('changeEmail')}
            </button>
          </div>

          <div>
            <p className='otp-info-text'>{t('enterReceivedCode')}</p>
            <OTPInput
              maxLength={5}
              value={otp}
              onChange={handleOtpChange}
              containerClassName={`otp-container ${verifyError ? 'invalid' : ''}`}
              disabled={hasReachedLimit}
              render={({ slots }) => (
                <div className="otp-box">
                  {slots.map((slot, idx) => (
                    <Slot key={idx} {...slot} />
                  ))}
                </div>
              )}
            />
            
            <div className='otp-validation-outer-container'>
              {error && <p className="error-text">{error}</p>}
              {verifyError && <p className="error-text">{verifyError}</p>}

              {!hasReachedLimit && (
                <div className='otp-validation-container'>
                  <button
                    className={`send-otp-button ${countdown > 0 ? 'disabled' : ''}`}
                    disabled={countdown > 0}
                    onClick={handleResendOtp}
                  >
                    {t('sendMeAgain')}
                  </button>
                  <p className='otp-countdown-container'>{formatTime(countdown)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="login-page-form-action">
          <p>{t('haveAnAccount')} <Link to="/login">{t("login")}</Link></p>
          <Button
            type="submit"
            text={t("confirmation")}
            color="blue"
            onClick={handleVerifyOTP}
            loader={loading}
            disabled={loading || hasReachedLimit}
          />
        </div>
      </div>
    </div>
  );
}

function Slot(props: SlotProps) {
  const isFilled = props.char !== null;
  return (
    <div className={`otp-slot ${props.isActive ? 'active' : ''}`}>
      <span className={`otp-char ${isFilled ? 'filled' : 'placeholder'}`}>
        {isFilled ? props.char : '•'}
      </span>
    </div>
  );
}
